/**
 * Per-family cap, shared with the server's Figma derivation
 * (figmaDesignTokens.ts imports it from here). The stored/derived set is read
 * back into a prompt that truncates at 2000 chars (tokenAuditAnalysis.ts), so
 * an uploaded set and a Figma-derived set must be capped identically or they
 * blow that truncation by different amounts.
 */
export const MAX_TOKENS_PER_FAMILY = 60;
/**
 * Ceiling on the serialized upload, shared by the server route (measures the
 * parsed body) and the extension dropzone (refuses the file client-side), so a
 * file the panel accepts can never be one the route would refuse.
 * A real design system exports tens of KB; a megabyte is already far past
 * anything legitimate.
 */
export const MAX_TOKEN_UPLOAD_BYTES = 1_000_000;
export const TOKEN_UPLOAD_TOO_LARGE = 'That file is too large to be a design tokens file. We accept up to 1 MB.';
/** Group nesting ceiling, mirroring MAX_DEPTH in figmaDesignTokens.ts. */
const MAX_DEPTH = 12;
/** Total nodes visited, mirroring MAX_NODES in figmaDesignTokens.ts. */
const MAX_NODES = 20_000;
/** Alias hops before we call it a cycle. Real token sets nest a handful deep. */
const MAX_ALIAS_HOPS = 10;
/**
 * User-facing copy. Exported so the upload surfaces render exactly these
 * strings instead of paraphrasing them, and so a copy change is a one-line
 * diff with a test behind it rather than a hunt through components.
 *
 * Each follows the house error formula: what happened, why in plain terms,
 * what to do next. No blame, no parser internals, no stack.
 */
export const TOKEN_UPLOAD_ERRORS = {
    /**
     * Raised BEFORE the parser, by whichever surface read the file (the server
     * route, the dashboard, the extension dropzone): the bytes were not JSON at
     * all. One definition so the three surfaces cannot drift.
     */
    notJson: "We couldn't read that file. It isn't valid JSON.",
    notObjectList: "We couldn't read that file. A design tokens file should be a JSON object, not a list.",
    notObject: "We couldn't read that file. A design tokens file should be a JSON object.",
    noTokens: "That file didn't contain any design tokens. We read the W3C format, where every token has a $value. Tokens Studio and Style Dictionary both export it.",
    noColors: "We found tokens in that file, but couldn't read any color values from them.",
};
/**
 * Stable analytics keys for a rejected upload, one per member of
 * TOKEN_UPLOAD_ERRORS plus the size refusal and a catch-all.
 *
 * WHY A KEY AND NOT THE COPY. The strings above are product copy: three
 * surfaces assert them verbatim and they are expected to be reworded. An
 * analytics series keyed to a sentence silently splits in two the day somebody
 * improves the wording, and nothing fails when it does. The key is the stable
 * half; the copy stays free to change.
 *
 * Emitters read `TOKEN_UPLOAD_REASONS.notJson` rather than the literal
 * `'not_json'`, so a renamed key breaks the build instead of quietly forking
 * the series.
 */
export const TOKEN_UPLOAD_REASONS = {
    notJson: 'not_json',
    notObjectList: 'not_object_list',
    notObject: 'not_object',
    noTokens: 'no_tokens',
    noColors: 'no_colors',
    tooLarge: 'too_large',
    /** Anything we do not recognise: a network failure, a 5xx body, a proxy page. */
    unknown: 'unknown',
};
/**
 * The copy -> key pairing, defined once beside the copy it pairs with.
 *
 * The mapping is REVERSE (message to key) because that is the only thing an
 * upload surface holds for a SERVER-side rejection: the API answers with a
 * rendered message, not a machine code, so the client learns the cause as a
 * sentence. Keeping the pairing here means the two halves cannot drift into
 * separate files.
 */
const REASON_BY_MESSAGE = new Map([
    [TOKEN_UPLOAD_ERRORS.notJson, TOKEN_UPLOAD_REASONS.notJson],
    [TOKEN_UPLOAD_ERRORS.notObjectList, TOKEN_UPLOAD_REASONS.notObjectList],
    [TOKEN_UPLOAD_ERRORS.notObject, TOKEN_UPLOAD_REASONS.notObject],
    [TOKEN_UPLOAD_ERRORS.noTokens, TOKEN_UPLOAD_REASONS.noTokens],
    [TOKEN_UPLOAD_ERRORS.noColors, TOKEN_UPLOAD_REASONS.noColors],
    [TOKEN_UPLOAD_TOO_LARGE, TOKEN_UPLOAD_REASONS.tooLarge],
]);
/**
 * Resolve a rendered rejection message to its stable analytics key.
 *
 * NEVER THROWS, and never returns null: an upload that failed for an
 * unrecognised reason is still an upload that failed, and dropping the event
 * would make the failure rate read better than it is. Unrecognised input
 * becomes `unknown`, which is a finding rather than a hole.
 */
export function tokenUploadFailureReason(message) {
    if (typeof message !== 'string')
        return TOKEN_UPLOAD_REASONS.unknown;
    return REASON_BY_MESSAGE.get(message.trim()) ?? TOKEN_UPLOAD_REASONS.unknown;
}
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/**
 * Flatten the document into `dot.path -> TokenNode`.
 *
 * `$type` inherits DOWN from groups, which the spec allows and Style Dictionary
 * exports rely on. A parser that demands `$type` on the token itself silently
 * drops every token in such a file, which would look to the user like "my file
 * is empty" rather than "your parser is wrong".
 */
function collectTokens(root) {
    const found = new Map();
    let visited = 0;
    const walk = (node, path, inheritedType) => {
        if (path.length > MAX_DEPTH || visited > MAX_NODES)
            return;
        const groupType = typeof node.$type === 'string' ? node.$type : inheritedType;
        for (const [key, child] of Object.entries(node)) {
            // `$`-prefixed keys are spec metadata ($type, $description, $extensions),
            // never tokens or groups.
            if (key.startsWith('$'))
                continue;
            if (!isPlainObject(child))
                continue;
            visited++;
            if (visited > MAX_NODES)
                return;
            const childPath = [...path, key];
            if ('$value' in child) {
                found.set(childPath.join('.'), {
                    value: child.$value,
                    type: typeof child.$type === 'string' ? child.$type : groupType,
                });
                continue;
            }
            walk(child, childPath, groupType);
        }
    };
    walk(root, [], null);
    return found;
}
/** `{color.base}` -> `color.base`, else null. */
function aliasTarget(value) {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}'))
        return null;
    const inner = trimmed.slice(1, -1).trim();
    return inner.length > 0 ? inner : null;
}
/**
 * Follow aliases to a concrete value.
 *
 * The hop cap is the ONLY cycle guard, and that is deliberate: a `seen` set was
 * here first and mutation testing proved it redundant, since the cap already
 * terminates `a -> b -> a` and every longer ring. Untested code that cannot be
 * made to fail is code to delete, so it went.
 */
function resolveValue(node, all) {
    let current = node.value;
    for (let hop = 0; hop < MAX_ALIAS_HOPS; hop++) {
        const target = aliasTarget(current);
        if (target === null)
            return current;
        const next = all.get(target);
        if (!next)
            return null;
        current = next.value;
    }
    return null;
}
/**
 * DTCG color values are hex strings in every export we target. Returns the
 * server derivation module's format exactly (`#RRGGBB` uppercase, opacity
 * separate) so the delta-E matcher never compares two spellings of one color.
 */
function parseHexColor(value) {
    if (typeof value !== 'string')
        return null;
    const raw = value.trim();
    if (!raw.startsWith('#'))
        return null;
    const body = raw.slice(1);
    if (!/^[0-9a-fA-F]+$/.test(body))
        return null;
    let hex;
    let alpha = 255;
    if (body.length === 3) {
        hex = body
            .split('')
            .map((ch) => ch + ch)
            .join('');
    }
    else if (body.length === 4) {
        const expanded = body
            .split('')
            .map((ch) => ch + ch)
            .join('');
        hex = expanded.slice(0, 6);
        alpha = parseInt(expanded.slice(6, 8), 16);
    }
    else if (body.length === 6) {
        hex = body;
    }
    else if (body.length === 8) {
        hex = body.slice(0, 6);
        alpha = parseInt(body.slice(6, 8), 16);
    }
    else {
        return null;
    }
    return {
        hex: `#${hex.toUpperCase()}`,
        opacity: Math.round((alpha / 255) * 100) / 100,
    };
}
export function parseDesignTokensJson(input) {
    const fail = (error) => ({
        ok: false,
        tokens: null,
        tokenCount: 0,
        error,
    });
    if (Array.isArray(input))
        return fail(TOKEN_UPLOAD_ERRORS.notObjectList);
    if (!isPlainObject(input))
        return fail(TOKEN_UPLOAD_ERRORS.notObject);
    const all = collectTokens(input);
    if (all.size === 0)
        return fail(TOKEN_UPLOAD_ERRORS.noTokens);
    const colors = [];
    for (const node of all.values()) {
        if (colors.length >= MAX_TOKENS_PER_FAMILY)
            break;
        // Tokens carrying an explicit non-color $type are skipped without comment;
        // an untyped token is still attempted, because plenty of hand-rolled
        // exports omit $type entirely and their values are still readable.
        if (node.type !== null && node.type !== 'color')
            continue;
        const parsed = parseHexColor(resolveValue(node, all));
        if (parsed)
            colors.push(parsed);
    }
    if (colors.length === 0)
        return fail(TOKEN_UPLOAD_ERRORS.noColors);
    return { ok: true, tokens: { colors }, tokenCount: colors.length, error: null };
}
//# sourceMappingURL=designTokensJson.js.map